import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Ban, CalendarPlus, ExternalLink, Loader2 } from 'lucide-react';
import type { DecisionItem, DecisionStage, QuickTime } from '@/api/calls';
import { getLostReasons } from '@/api/crmCard';
import { crmKeys } from '@/hooks/useDealCard';
import {
  ageWords, decideAction, decisionMeta, hasProposal, lostStageOf, openStages, type DecisionAction,
} from '@/lib/callDecisions';
import { DirIcon } from './CallRow';

interface Props {
  item: DecisionItem;
  stages: DecisionStage[];
  quickTimes: QuickTime[];
  project: string;
  busy: boolean;
  error: string | null;
  onAction: (a: DecisionAction) => void;
  onMeeting: () => void;
  onOpenDeal: () => void;
}

type Mode = 'view' | 'edit' | 'lost';

const stepWhen = (iso: string) =>
  new Intl.DateTimeFormat('ru-RU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** ISO → значение поля datetime-local по часам телефона. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Итог звонка «Ждут вашего решения» — мобильная версия DecisionCard веб-CRM:
 * Принять · Решить/Изменить · Оставить как есть; ниже — Встреча · Отказ · Карточка.
 */
export function DecisionCard({ item, stages, quickTimes, project, busy, error, onAction, onMeeting, onOpenDeal }: Props) {
  const [mode, setMode] = useState<Mode>('view');
  const s = item.suggestion;
  const proposal = hasProposal(item);
  const lost = lostStageOf(stages);
  const name = item.person?.name || item.deal.title || item.phone || 'Клиент';
  const openTasks = (item.tasks?.proposals ?? []).filter((p) => !p.decision).length;

  return (
    <article className="dec" aria-label={`Итог звонка: ${name}`}>
      <div className="dtop">
        <span className={`dir ${item.direction === 'incoming' ? 'in' : 'out'}`}><DirIcon c={{ direction: item.direction, status: 'completed' }} /></span>
        <span className="dname"><b>{name}</b><small>{decisionMeta(item)}</small></span>
        <span className={`age tnum${item.old ? ' old' : ''}`}>{ageWords(item.ageMinutes)}</span>
      </div>

      {item.summary && <p>{item.summary}</p>}

      {mode === 'view' && proposal && s && (
        <div className="prop" aria-label="Что предлагает система">
          <span className="pl">Система предлагает{s.confidence !== null ? ` · ${s.confidence}%` : ''}</span>
          <span>
            Этап: {s.stageId
              ? <>{item.deal.stageName} <ArrowRight size={12} aria-hidden style={{ display: 'inline', verticalAlign: -1 }} /> <b>{s.stageName}</b></>
              : 'оставить как есть'}
          </span>
          {s.nextStepTitle && <span>{s.nextStepTitle}{s.nextStepAt ? ` — ${stepWhen(s.nextStepAt)}` : ''}</span>}
          {s.lostReason && <span>Причина отказа: {s.lostReason}</span>}
          {s.gateOk === false && s.gateMissing.length > 0 && (
            <span style={{ color: 'var(--warn)' }}>Для перевода не хватает: {[...new Set(s.gateMissing.map((m) => m.message))].join(', ')}</span>
          )}
        </div>
      )}
      {mode === 'view' && item.kind === 'manual' && <p className="why">{item.why ?? 'Предложения нет — решите сами'}</p>}
      {item.kind === 'tasks' && <p className="why">Этап уже решён. Поручения из разговора ({openTasks}) решите в CRM.</p>}

      {mode === 'edit' && (
        <EditForm item={item} stages={openStages(stages)} quickTimes={quickTimes} busy={busy}
          onCancel={() => setMode('view')} onSubmit={(a) => onAction(a)} />
      )}
      {mode === 'lost' && lost && (
        <LostForm project={project} busy={busy} onCancel={() => setMode('view')}
          onSubmit={(r) => onAction({ kind: 'lost', stageId: lost.id, ...r })} />
      )}

      {error && <p className="derr" role="alert">{error}</p>}

      {mode === 'view' && item.kind !== 'tasks' && (
        <>
          <div className="acts">
            {proposal && (
              <button type="button" className="pri" disabled={busy} onClick={() => onAction({ kind: 'accept' })}>
                {busy ? <Loader2 className="spin-i" size={15} aria-label="Сохраняем" /> : 'Принять'}
              </button>
            )}
            <button type="button" className={proposal ? '' : 'pri'} disabled={busy} onClick={() => setMode('edit')}>{proposal ? 'Изменить' : 'Решить'}</button>
            <button type="button" disabled={busy} onClick={() => onAction({ kind: 'keep' })}>Оставить как есть</button>
          </div>
          <div className="acts2">
            <button type="button" disabled={busy} onClick={onMeeting}><CalendarPlus aria-hidden />Назначить встречу</button>
            {lost && <button type="button" disabled={busy} onClick={() => setMode('lost')}><Ban aria-hidden />Отказ</button>}
            <button type="button" onClick={onOpenDeal}><ExternalLink aria-hidden />Открыть карточку</button>
          </div>
        </>
      )}
      {mode === 'view' && item.kind === 'tasks' && (
        <div className="acts2"><button type="button" onClick={onOpenDeal}><ExternalLink aria-hidden />Открыть карточку</button></div>
      )}
    </article>
  );
}

function EditForm({ item, stages, quickTimes, busy, onCancel, onSubmit }: {
  item: DecisionItem; stages: DecisionStage[]; quickTimes: QuickTime[]; busy: boolean;
  onCancel: () => void; onSubmit: (a: DecisionAction) => void;
}) {
  const s = item.suggestion;
  const [stageId, setStageId] = useState(s?.stageId ?? item.deal.stageId ?? '');
  const [withStep, setWithStep] = useState(true);
  const [title, setTitle] = useState(s?.nextStepTitle ?? (item.nextStep || 'Перезвонить'));
  const [at, setAt] = useState<string | null>(s?.nextStepAt ?? null);
  const [custom, setCustom] = useState(false);
  const chosen = quickTimes.find((q) => q.at === at)?.key ?? (at ? 'custom' : null);
  const ok = !withStep || (title.trim().length > 0 && !!at);

  return (
    <div className="form">
      <span className="lbl">Этап</span>
      <div className="chips" role="radiogroup" aria-label="Этап">
        {stages.map((st) => (
          <button key={st.id} type="button" role="radio" aria-checked={stageId === st.id} className={`chip${stageId === st.id ? ' on' : ''}`} onClick={() => setStageId(st.id)}>
            {st.name}{st.id === item.deal.stageId ? ' · сейчас' : ''}
          </button>
        ))}
      </div>
      <label className="toggle"><input type="checkbox" checked={withStep} onChange={(e) => setWithStep(e.target.checked)} />Следующий шаг</label>
      {withStep && (
        <>
          <label className="ifield">Что сделать<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например, перезвонить" /></label>
          <div className="chips" role="radiogroup" aria-label="Когда">
            {quickTimes.map((q) => (
              <button key={q.key} type="button" role="radio" aria-checked={chosen === q.key} className={`chip${chosen === q.key ? ' on' : ''}`} onClick={() => { setAt(q.at); setCustom(false); }}>{q.label}</button>
            ))}
            <button type="button" role="radio" aria-checked={custom || chosen === 'custom'} className={`chip${custom || chosen === 'custom' ? ' on' : ''}`} onClick={() => setCustom(true)}>Своя дата</button>
          </div>
          {(custom || chosen === 'custom') && (
            <label className="ifield">Дата и время
              <input type="datetime-local" value={toLocalInput(at)} onChange={(e) => setAt(e.target.value ? new Date(e.target.value).toISOString() : null)} />
            </label>
          )}
          {at && <span className="lbl">Задача встанет на {stepWhen(at)}</span>}
        </>
      )}
      <div className="btnrow">
        <button type="button" className="primary" disabled={busy || !ok} onClick={() => onSubmit(decideAction(item, { stageId, withStep, title, at }))}>
          {busy ? <Loader2 className="spin-i" aria-hidden /> : null}Сохранить
        </button>
        <button type="button" className="ghost" disabled={busy} onClick={onCancel}>Отмена</button>
      </div>
    </div>
  );
}

function LostForm({ project, busy, onCancel, onSubmit }: {
  project: string; busy: boolean; onCancel: () => void;
  onSubmit: (r: { reasonId?: string; comment?: string; reasonText?: string }) => void;
}) {
  const reasons = useQuery({ queryKey: crmKeys.reasons(project), queryFn: getLostReasons, staleTime: 10 * 60_000 });
  const [picked, setPicked] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const list = reasons.data ?? [];
  const needText = reasons.isFetched && list.length === 0;
  const chosen = list.find((r) => r.id === picked);
  const ok = needText ? comment.trim().length > 0 : !!chosen && (!chosen.needsComment || comment.trim().length > 0);
  return (
    <div className="form">
      <span className="lbl">Почему отказ?</span>
      {reasons.isPending && <span className="lbl">Загружаем причины…</span>}
      {list.length > 0 && (
        <div className="chips" role="radiogroup" aria-label="Причина отказа">
          {list.map((r) => (
            <button key={r.id} type="button" role="radio" aria-checked={picked === r.id} className={`chip${picked === r.id ? ' on' : ''}`} onClick={() => setPicked(r.id)}>{r.name}</button>
          ))}
        </div>
      )}
      <label className="ifield">{needText ? 'Причина отказа' : chosen?.needsComment ? 'Пояснение (обязательно)' : 'Пояснение (необязательно)'}
        <input value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>
      <div className="btnrow">
        <button type="button" className="primary danger" disabled={busy || !ok}
          onClick={() => onSubmit(needText ? { reasonText: comment.trim() } : { reasonId: picked!, comment: comment.trim() || undefined })}>
          Отказ
        </button>
        <button type="button" className="ghost" disabled={busy} onClick={onCancel}>Отмена</button>
      </div>
    </div>
  );
}
